import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DataTable, { Column } from "@/components/ui/DataTable";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useGetStockCountsQuery } from "@/redux/services/inventory/stockCount.api";
import {
  StockCountListItem,
  StockCountStatus,
} from "@/redux/services/inventory/inventory.type";
import { cn } from "@/lib/utils";
import { usePagination, useToggle } from "@/lib/hooks";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import { Plus } from "lucide-react";
import { usePermissions } from "@/hooks/usePermissions";
import StartStockCountModal from "./StartStockCountModal";
import {
  STOCK_COUNT_STATUS_LABELS,
  STOCK_COUNT_STATUS_STYLES,
} from "./stockCountStatus";

interface StockCountsListProps {
  restaurantId: string;
}

const SENTINEL_ALL = "__all__";

const StockCountsList = ({ restaurantId }: StockCountsListProps) => {
  const navigate = useNavigate();
  const { page, limit, onChange } = usePagination(25);
  const [status, setStatus] = useState<StockCountStatus | undefined>();
  const { canManage } = usePermissions();
  const canManageInventory = canManage("inventory");

  useEffect(() => {
    onChange(limit, 1);
  }, [status]);

  const { data, isFetching } = useGetStockCountsQuery({
    restaurantId,
    query: { page, limit, ...(status ? { status } : {}) },
  });
  const { isOpen: isStartOpen, open: openStart, close: closeStart } =
    useToggle();

  const columns: Column[] = [
    {
      key: "countedAt",
      label: "Date",
      nowrap: true,
      formatter: (_v, row: StockCountListItem) => (
        <span className="font-medium">
          {format(parseISO(row.countedAt), "d MMMM yyyy", { locale: fr })}
        </span>
      ),
    },
    {
      key: "status",
      label: "Statut",
      formatter: (_v, row: StockCountListItem) => (
        <span
          className={cn(
            "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium",
            STOCK_COUNT_STATUS_STYLES[row.status]
          )}
        >
          {STOCK_COUNT_STATUS_LABELS[row.status]}
        </span>
      ),
    },
    {
      key: "linesCount",
      label: "Lignes",
      formatter: (_v, row: StockCountListItem) => `${row.linesCount}`,
    },
    {
      key: "finalizedAt",
      label: "Finalisé le",
      nowrap: true,
      formatter: (_v, row: StockCountListItem) =>
        row.finalizedAt
          ? format(parseISO(row.finalizedAt), "d MMM yyyy, HH:mm", {
              locale: fr,
            })
          : "—",
    },
    {
      key: "notes",
      label: "Notes",
      formatter: (_v, row: StockCountListItem) =>
        row.notes ? (
          <span className="block max-w-xs truncate" title={row.notes}>
            {row.notes}
          </span>
        ) : (
          "—"
        ),
    },
  ];

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="max-w-2xl">
          <h2 className="text-base font-semibold text-card-foreground">
            Contrôle de stock
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Comparez périodiquement le stock théorique du système au stock réel
            compté en rayon. Chaque écart non nul génère un ajustement de
            correction lors de la finalisation, afin de garder l'inventaire du
            logiciel aligné avec la réalité.
          </p>
        </div>
        {canManageInventory && (
          <Button
            type="button"
            onClick={openStart}
            className="flex-shrink-0"
          >
            <Plus className="h-4 w-4 mr-1" />
            Démarrer un inventaire
          </Button>
        )}
      </div>

      <div className="flex items-center">
        <div className="w-full sm:w-52">
          <Select
            value={status ?? SENTINEL_ALL}
            onValueChange={(v) =>
              setStatus(
                v === SENTINEL_ALL ? undefined : (v as StockCountStatus)
              )
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Tous les statuts" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={SENTINEL_ALL}>Tous les statuts</SelectItem>
              {(
                Object.keys(STOCK_COUNT_STATUS_LABELS) as StockCountStatus[]
              ).map((s) => (
                <SelectItem key={s} value={s}>
                  {STOCK_COUNT_STATUS_LABELS[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <DataTable
        data={data?.results ?? []}
        columns={columns}
        loading={isFetching}
        totalCount={data?.count ?? 0}
        currentPage={page}
        pageSize={limit}
        onPageChange={(p) => onChange(limit, p)}
        onPageSizeChange={(size) => onChange(size, 1)}
        onRowClick={(row: StockCountListItem) =>
          navigate(`/inventory/stock-counts/${row.id}`)
        }
        searchable={false}
        emptyMessage="Aucun inventaire physique"
      />

      <StartStockCountModal
        isOpen={isStartOpen}
        onClose={closeStart}
        restaurantId={restaurantId}
        onStarted={(uuid) => {
          closeStart();
          navigate(`/inventory/stock-counts/${uuid}`);
        }}
      />
    </div>
  );
};

export default StockCountsList;
